import React, { useState } from 'react';
import { X, FileDown } from 'lucide-react';
import apiService from '../services/api';

const BUDGET_RANGES = [
  'Under 25 Lakhs',
  '25 Lakhs - 50 Lakhs',
  '50 Lakhs - 1 Crore',
  '1 Crore - 2 Crore',
  'Above 2 Crore',
];

interface BrochureDownloadModalProps {
  isOpen: boolean;
  propertyId: number;
  propertyTitle: string;
  brochureId: number;
  brochureFilename?: string;
  onClose: () => void;
}

export const BrochureDownloadModal: React.FC<BrochureDownloadModalProps> = ({
  isOpen,
  propertyId,
  propertyTitle,
  brochureId,
  brochureFilename,
  onClose,
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [budgetRange, setBudgetRange] = useState(BUDGET_RANGES[0]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const resetAndClose = () => {
    setName('');
    setEmail('');
    setMobile('');
    setBudgetRange(BUDGET_RANGES[0]);
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(mobile.trim())) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const response = await apiService.submitBrochureLead({
        name: name.trim(),
        email: email.trim(),
        phone: mobile.trim(),
        budget_range: budgetRange,
        property_id: propertyId,
        brochure_id: brochureId,
      });

      if (response.success) {
        const downloadUrl = apiService.getBrochureDownloadUrl(brochureId);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = brochureFilename || 'brochure.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        resetAndClose();
      } else {
        setError(response.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative">
        <button
          onClick={resetAndClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 sm:p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Download brochure</h2>
          <p className="text-gray-500 mb-6">
            Share a few details and we'll unlock the brochure for <span className="font-medium text-gray-700">{propertyTitle}</span>.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full name</label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                <input
                  type="tel"
                  required
                  placeholder="10-digit mobile"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Investment budget</label>
              <select
                value={budgetRange}
                onChange={(e) => setBudgetRange(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors bg-white"
              >
                {BUDGET_RANGES.map((range) => (
                  <option key={range} value={range}>{range}</option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 bg-emerald-800 hover:bg-emerald-900 text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileDown className="w-5 h-5" />
              {submitting ? 'Please wait...' : 'Download brochure'}
            </button>

            <p className="text-center text-xs text-gray-400">We respect your privacy. No spam, ever.</p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BrochureDownloadModal;
